import type { ReactNode } from 'react';

/**
 * A status message with an aria-live region so async results are announced (WCAG 4.1.3). `tone`
 * drives BOTH the styling and a leading text label, so meaning never depends on color alone.
 */
export function StatusMessage({
  tone = 'info',
  children,
}: {
  tone?: 'ok' | 'warn' | 'err' | 'info';
  children: ReactNode;
}) {
  const cls =
    tone === 'ok'
      ? 'status status--ok'
      : tone === 'warn'
        ? 'status status--warn'
        : tone === 'err'
          ? 'status status--err'
          : 'status';
  const label = tone === 'ok' ? 'Success: ' : tone === 'warn' ? 'Warning: ' : tone === 'err' ? 'Error: ' : '';
  return (
    <p className={cls} role="status" aria-live="polite">
      {label && <strong>{label}</strong>}
      {children}
    </p>
  );
}

/** A polite loading indicator (announced to assistive tech). */
export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <p className="status" role="status" aria-live="polite">
      {label}
    </p>
  );
}

/** An error panel with a retry affordance. */
export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="status status--err" role="alert">
      <strong>Error: </strong>
      {message}
      {onRetry && (
        <>
          {' '}
          <button type="button" className="secondary" onClick={onRetry}>
            Retry
          </button>
        </>
      )}
    </div>
  );
}
